import React, { useEffect, useState } from "react";
import { Mail, Printer, Send, ShieldCheck, Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * SendI589EmailCard — Dashboard widget that lets the logged-in user (PIN-auth)
 * email the blank USCIS I-589 PDF to any email address for printing.
 *
 * Zero-Data: the recipient email is sent ONCE to the backend and is NEVER
 * persisted in MongoDB. The backend logs only {user_id, sent_at, status}.
 *
 * Rate limit: up to 3 total sends per PIN (enforced server-side).
 */
export default function SendI589EmailCard() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [usage, setUsage] = useState(null); // {sent, max, remaining}
  const [justSent, setJustSent] = useState(false);

  const loadUsage = async () => {
    try {
      const { data } = await api.get("/email/i589/remaining");
      setUsage(data);
    } catch {
      setUsage(null);
    }
  };

  useEffect(() => {
    loadUsage();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      toast.error("Por favor escribe un correo válido");
      return;
    }
    if (usage && usage.remaining <= 0) {
      toast.error(`Ya enviaste el I-589 ${usage.max} veces (el máximo).`);
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/email/i589/send", { recipient_email: trimmed });
      setJustSent(true);
      toast.success("Correo enviado · revisa tu bandeja en unos minutos");
      setUsage({ sent: (usage?.sent || 0) + 1, max: data.max, remaining: data.remaining });
      setEmail("");
      setTimeout(() => setJustSent(false), 4500);
    } catch (err) {
      const detail = err?.response?.data?.detail || "No se pudo enviar el correo";
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  const remaining = usage?.remaining ?? null;
  const isLimitReached = remaining !== null && remaining <= 0;

  let submitContent;
  if (loading) {
    submitContent = (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Enviando…</>);
  } else if (justSent) {
    submitContent = (<><CheckCircle2 className="w-4 h-4 mr-2" />Enviado</>);
  } else {
    submitContent = (<><Send className="w-4 h-4 mr-2" />Enviarme el I-589</>);
  }

  return (
    <div
      data-testid="send-i589-email-card"
      className="rounded-2xl border-2 border-zinc-200 bg-white p-5 mb-4"
    >
      <div className="flex items-start gap-3 mb-3">
        <div className="w-11 h-11 rounded-xl bg-indigo-100 flex items-center justify-center flex-shrink-0">
          <Printer className="w-5 h-5 text-indigo-700" strokeWidth={2.5} />
        </div>
        <div className="flex-1">
          <h3 className="font-bold text-base leading-tight text-zinc-900">
            Imprimir el Formulario I-589
          </h3>
          <p className="text-xs text-zinc-600 mt-0.5">
            Te lo enviamos a tu correo para que lo imprimas en hojas tamaño carta.
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-2.5">
        <label htmlFor="i589-email" className="sr-only">Correo electrónico</label>
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400 pointer-events-none" />
          <Input
            id="i589-email"
            data-testid="send-i589-email-input"
            type="email"
            inputMode="email"
            autoComplete="email"
            placeholder="tucorreo@ejemplo.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading || isLimitReached}
            className="pl-9 h-11"
            required
          />
        </div>

        <Button
          type="submit"
          data-testid="send-i589-email-submit"
          disabled={loading || isLimitReached || !email}
          className="w-full h-11 bg-indigo-700 hover:bg-indigo-800 text-white font-semibold"
        >
          {submitContent}
        </Button>
      </form>

      <div className="mt-3 flex items-center gap-2 text-[11px] text-zinc-500 leading-snug">
        <ShieldCheck className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" />
        <span>
          No guardamos tu correo. Solo lo usamos para enviarte el manual y luego se descarta.
        </span>
      </div>

      {usage && (
        <p
          data-testid="send-i589-usage"
          className={`mt-2 text-[11px] ${isLimitReached ? "text-rose-600 font-semibold" : "text-zinc-500"}`}
        >
          {isLimitReached
            ? `Ya enviaste el I-589 ${usage.max} veces (el máximo).`
            : `Envíos restantes: ${usage.remaining} de ${usage.max}.`}
        </p>
      )}
    </div>
  );
}
